import { createContext, useContext, useEffect, useState, useCallback } from "react";

const STRINGS = {
  en: {
    nav: { categories: "Categories", shop: "Shop", about: "About", contact: "Contact", cart: "Cart" },
    whatsapp: { online_now: "Online now — Chat with us", closed_today: "Closed today — back tomorrow" },
    loading: { subtitle: "Treats. Toys. Tail-wags.", progress: "Fetching the goodies…" },
    hero: {
      badge: "Pay your way • Cash on Delivery available",
      title1: "Everything Your", cat: "Cat", and: "&", dog: "Dog",
      title2: "Need,", delivered: "delivered to your door.",
      subtitle: "Premium food, toys, grooming and care essentials — handpicked for your furry family. Pay your way: Vodafone Cash, InstaPay, Fawry, or Cash on Delivery.",
      shop_dog: "Shop Dog Products", shop_cat: "Shop Cat Products",
      fast_delivery: "Same-day delivery in Cairo, Giza & nearby", trusted: "100% trusted brands", loved: "Loved by 10k+ pets",
      dogs_card: "Dogs", dogs_desc: "Food, leashes, beds & beyond.",
      cats_card: "Cats", cats_desc: "Litter, toys, treats & comfort.",
      pay_title: "Multiple payment options.",
      pay_desc: "Vodafone Cash, InstaPay, Fawry or Cash on Delivery.",
    },
    categories: {
      tag: "SHOP BY CATEGORY",
      title_pre: "Pick the perfect", title_word: "aisle.",
      subtitle: "12 essentials covered — food, toys, grooming, comfort, health & more, for both cats and dogs.",
      view: "View Products →",
      labels: {
        "Dog Food": "Dog Food", "Cat Food": "Cat Food", "Treats": "Treats", "Toys": "Toys",
        "Grooming": "Grooming", "Litter & Cleaning": "Litter & Cleaning",
        "Collars/Leashes": "Collars/Leashes", "Beds & Comfort": "Beds & Comfort",
        "Bowls & Feeders": "Bowls & Feeders", "Health & Care": "Health & Care",
        "Training Essentials": "Training Essentials", "Travel & Carriers": "Travel & Carriers",
      },
      desc: {
        "Dog Food": "Daily kibble & wet meals for dogs.",
        "Cat Food": "Tasty meals for kittens & adult cats.",
        "Treats": "Training rewards & guilty-free snacks.",
        "Toys": "Fetch, chase, chew — the fun stuff.",
        "Grooming": "Shampoos, brushes & spa essentials.",
        "Litter & Cleaning": "Clumping litter & cleaning gear.",
        "Collars/Leashes": "Walks, harnesses & ID tags.",
        "Beds & Comfort": "Cozy beds, blankets & cat caves.",
        "Bowls & Feeders": "Bowls, feeders & water fountains.",
        "Health & Care": "Vitamins, dental & first-aid.",
        "Training Essentials": "Clickers, pads & puppy starter kits.",
        "Travel & Carriers": "Carriers, harnesses & on-the-go gear.",
      },
    },
    products: {
      tag: "FRESH PICKS", title_pre: "Today's", title_mid: "tail-wag", title_post: "worthy.",
      tabs: {
        all: "All", dog: "Dogs", cat: "Cats", Food: "Food", Toys: "Toys", Treats: "Treats",
        Grooming: "Grooming", Litter: "Litter", Accessories: "Accessories", Health: "Health",
      },
      in_stock: "In Stock", out: "Out", add_to_cart: "Add to Cart",
      out_of_stock: "Out of Stock",
      only_left: "Only {n} left!",
      low_stock: "Low: {n} left",
      available: "In stock",
      urgency_almost: "🔥 Almost gone — only {n} left!",
      urgency_limited: "⚡ Limited stock — {n} available",
      loading: "Loading goodies…", empty: "No products match this filter yet.",
      clear_filter: "Clear filter",
    },
    cart: {
      title: "Your Cart", empty: "Your cart is empty. Time to spoil them! 🐾",
      shipping: "Shipping Method",
      city_october: "October", city_october_fee: "60 EGP",
      city_zayed: "Zayed", city_zayed_fee: "90 EGP",
      city_beverly_hills: "Beverly Hills", city_beverly_hills_fee: "100 EGP",
      city_giza_harem: "Giza/Harem", city_giza_harem_fee: "130 EGP",
      subtotal: "Subtotal", ship_label: "Shipping", total: "Total",
      discount: "Discount", discount_code: "Discount Code", apply: "Apply",
      discount_applied: "5% discount applied", discount_invalid: "Invalid or expired discount code",
      remove_code: "Remove Code",
      checkout: "Proceed to Checkout",
      note: "Pay your way after placing the order.",
      added: "added to cart",
      min_order_message: "Minimum order value is 200 EGP. Add {amount} EGP more to proceed to checkout.",
    },
    checkout: {
      title: "Place Your Order",
      full_name: "Full Name *", phone: "Phone Number *",
      whatsapp: "WhatsApp (if different)", email: "Email (optional)",
      city: "City *", area: "Area *", address: "Full Address *",
      address_notes: "Building / Floor / Apartment",
      delivery_time: "Preferred Delivery Time", delivery_ph: "e.g. After 6 PM",
      order_notes: "Order Notes",
      payment_method: "Payment Method",
      payment_reference: "Payment Reference / Transaction ID",
      payment_reference_ph: "After sending payment, paste the transaction ID here",
      payment_reference_optional: "(Optional — you can also send it later via WhatsApp)",
      ref_phone: "Phone number used for payment",
      ref_phone_ph: "The phone number you sent the payment from",
      ref_receipt: "Receipt Number",
      ref_receipt_ph: "The receipt number from your Fawry payment",
      pay_inst_title: "Payment Instructions",
      vodafone_inst: "Send {amount} EGP to Vodafone Cash number: {phone}",
      vodafone_step: "Then enter your transaction reference below.",
      instapay_inst: "Send {amount} EGP via InstaPay to: {phone}",
      instapay_step: "Then enter your transaction reference below.",
      fawry_inst: "Send {amount} EGP to Fawry Wallet / Yellow Card number: {phone}",
      fawry_step: "Then enter your transaction reference below.",
      cod_inst: "You'll pay {amount} EGP in cash when our courier delivers your order. No advance payment required.",
      subtotal: "Subtotal", ship_label: "Shipping", total: "Total",
      submit: "Place Order", submitting: "Placing your order…",
      placed_toast: "Order placed! 🎉",
    },
    confirm: {
      title: "Order placed! 🐾",
      message_pre: "Our team will contact you shortly on", message_post: "to confirm your order.",
      order_number: "Order Number", status: "Status",
      customer: "Customer", city_area: "City / Area",
      shipping: "Shipping", payment: "Payment", subtotal: "Subtotal", total: "Total",
      pay_status: "Payment Status",
      next_steps: "Next steps",
      save_note: "Save your order number — you can reference it when we call.",
      continue: "Continue Shopping",
    },
    about: {
      tag: "WHY PET MUNCHIES",
      title: "Built for pet parents who care.",
      intro: "We started Pet Munchies because finding quality, affordable pet supplies in Egypt shouldn't feel like a treasure hunt. We do the picking — you do the spoiling.",
      trust: [
        ["Trusted Pet Products", "Hand-picked brands from licensed vets and breeders."],
        ["Same-day Delivery", "Same-day delivery in Cairo, Giza & most nearby areas."],
        ["Friendly Support", "Real humans on WhatsApp — we love pets too."],
        ["Cats & Dogs Only", "Specialised range, never a 'jack-of-all'."],
        ["Easy Ordering", "Vodafone Cash, InstaPay, Fawry or Cash on Delivery."],
        ["Loved by 10k+", "Wagging tails and purring kittens since 2024."],
      ],
    },
    contact: {
      tag: "WE'RE HERE",
      title_pre: "Talk to a real", title_word: "pet person",
      subtitle: "Questions about food, breed-specific picks, or your order? Reach out — we usually reply within the hour during business hours.",
      whatsapp: "WhatsApp", phone: "Phone", instagram: "Instagram", facebook: "Facebook",
      hours: "Business Hours", sat_thu: "Sat – Thu", friday: "Friday",
      open_247: "Open 24/7", closed: "Closed",
      outside: "Outside business hours, drop us a WhatsApp message — we'll get back first thing in the morning. All orders placed late are reviewed at opening.",
    },
    footer: { rights: "All trademarks belong to their respective owners.", made: "Made with 🐾 in Egypt." },
    payments: {
      "Vodafone Cash": "Vodafone Cash",
      "Instapay": "InstaPay",
      "FawryPay": "Fawry Wallet/Yellow Card",
      "Cash on Delivery": "Cash on Delivery",
      coming_soon: "Coming Soon",
    },
    search: {
      placeholder: "Search food, toys, brands…",
      title: "Search products", desc: "Find by name, brand, category or description.",
      searching: "Searching…", no_results: "No products found. Try a different word.",
    },
    tracking: {
      nav: "Track Order",
      title: "Track Your Order", desc: "Enter your order number and the phone number you used at checkout.",
      order_number: "Order Number", phone: "Phone Number",
      submit: "Track Order", searching: "Searching…",
      not_found: "Order not found. Check your order number and phone.",
      payment_status: "Payment Status", shipping_status: "Shipping Status",
      search_again: "Track Another Order",
    },
    banner: {
      text: "Use code {code} for 5% off your first order!",
      copy_cta: "Copy Code", copied: "Code copied — happy shopping! 🐾",
    },
    pdp: {
      back: "Back to Shop", not_found: "Product not found",
      not_found_desc: "This product may have been removed or the link is incorrect.",
      brand: "Brand", size: "Size", weight: "Weight", age_stage: "Best For",
      ingredients: "Ingredients", feeding: "Feeding Instructions",
      details: "Product Details",
    },
    reviews: {
      title: "Customer Reviews", based_on: "Based on {n} review", based_on_plural: "Based on {n} reviews",
      no_reviews: "No reviews yet. Be the first to share your experience!",
      verified: "Verified Purchase",
      purchase_hint: "Only customers who completed an order can leave a review — we'll email you a review link after delivery.",
      your_name: "Your Name (shown publicly)", your_rating: "Your Rating *", your_review: "Your Review *",
      submit: "Submit Review", submitting: "Submitting…",
      thanks: "Thanks! Your review has been submitted and is awaiting moderation.",
      rate_order_title: "Rate Your Order", rate_order_desc: "Thanks for shopping with us, {name}! Tell us what you and your pet thought.",
      already_reviewed: "You already reviewed this — thank you!",
      invalid_title: "Review link not found",
      invalid_desc: "This review link is invalid or has expired. Reviews can only be submitted after an order is delivered.",
    },
  },
  ar: {
    nav: { categories: "الفئات", shop: "المتجر", about: "عننا", contact: "تواصل", cart: "العربة" },
    whatsapp: { online_now: "متاحين الآن — تواصل معنا", closed_today: "قفلين النهاردة — نرجع بكرة" },
    loading: { subtitle: "حلويات. لعب. ذيول مبسوطة.", progress: "جاري التحميل…" },
    hero: {
      badge: "ادفع بطريقتك • الدفع عند الاستلام متاح",
      title1: "كل احتياجات", cat: "قطتك", and: "و", dog: "كلبك",
      title2: "،", delivered: "بنوصلهم لحد بابك.",
      subtitle: "أكل ولعب ومستلزمات نضافة وعناية — منتقاة بإيد خبيرة لعيلتك ذات الأربع أرجل. ادفع بطريقتك: فودافون كاش، إنستاباي، فوري، أو كاش عند الاستلام.",
      shop_dog: "تسوق منتجات الكلاب", shop_cat: "تسوق منتجات القطط",
      fast_delivery: "توصيل في نفس اليوم في القاهرة والجيزة والمناطق القريبة", trusted: "ماركات موثوقة ١٠٠٪", loved: "محبوب من ١٠ آلاف+ حيوان",
      dogs_card: "كلاب", dogs_desc: "أكل، مقاود، سراير وأكتر.",
      cats_card: "قطط", cats_desc: "رمل، لعب، حلويات وراحة.",
      pay_title: "طرق دفع متعددة.",
      pay_desc: "فودافون كاش، إنستاباي، فوري، أو كاش عند الاستلام.",
    },
    categories: {
      tag: "تسوق حسب الفئة",
      title_pre: "اختار الـ", title_word: "المثالي.",
      subtitle: "١٢ فئة تغطي كل احتياجاتك — أكل، لعب، نضافة، راحة، صحة وأكتر، للقطط والكلاب.",
      view: "شوف المنتجات →",
      labels: {
        "Dog Food": "أكل كلاب", "Cat Food": "أكل قطط", "Treats": "حلويات", "Toys": "لعب",
        "Grooming": "نضافة وتجميل", "Litter & Cleaning": "رمل ونضافة",
        "Collars/Leashes": "أطواق ومقاود", "Beds & Comfort": "سراير وراحة",
        "Bowls & Feeders": "أطباق وعلافات", "Health & Care": "صحة وعناية",
        "Training Essentials": "مستلزمات تدريب", "Travel & Carriers": "حقائب سفر",
      },
      desc: {
        "Dog Food": "أكل يومي ومعلبات للكلاب.",
        "Cat Food": "وجبات لذيذة للقطط الصغيرة والكبيرة.",
        "Treats": "مكافآت تدريب وسناكس صحية.",
        "Toys": "لعب رمي وعض ولهو.",
        "Grooming": "شامبو وفرش ومستلزمات نضافة.",
        "Litter & Cleaning": "رمل ومستلزمات نضافة.",
        "Collars/Leashes": "أطواق وحزم ومقاود.",
        "Beds & Comfort": "سراير ومفارش وكهوف للقطط.",
        "Bowls & Feeders": "أطباق وعلافات ونوافير.",
        "Health & Care": "فيتامينات ونظافة فم وإسعافات.",
        "Training Essentials": "كليكر ومنشفات وكتاب تدريب.",
        "Travel & Carriers": "حقائب وحبال للسفر.",
      },
    },
    products: {
      tag: "منتجاتنا المختارة", title_pre: "منتجات", title_mid: "بتفرّح", title_post: "النهاردة.",
      tabs: {
        all: "الكل", dog: "كلاب", cat: "قطط", Food: "أكل", Toys: "لعب", Treats: "حلويات",
        Grooming: "نضافة", Litter: "رمل", Accessories: "إكسسوارات", Health: "صحة",
      },
      in_stock: "متوفر", out: "نفد", add_to_cart: "ضيف للعربة",
      out_of_stock: "نفد المخزون",
      only_left: "آخر {n} بس!",
      low_stock: "متبقي {n}",
      available: "متوفر",
      urgency_almost: "🔥 قرّب يخلص — آخر {n}!",
      urgency_limited: "⚡ الكمية محدودة — {n} متاح",
      loading: "جاري التحميل…", empty: "مفيش منتجات في الفئة دي.",
      clear_filter: "إلغاء الفلتر",
    },
    cart: {
      title: "عربتك", empty: "العربة فاضية. وقت الدلع! 🐾",
      shipping: "طريقة الشحن",
      city_october: "أكتوبر", city_october_fee: "60 جنيه",
      city_zayed: "زايد", city_zayed_fee: "90 جنيه",
      city_beverly_hills: "بيفرلي هيلز", city_beverly_hills_fee: "100 جنيه",
      city_giza_harem: "الجيزة/الهرم", city_giza_harem_fee: "130 جنيه",
      subtotal: "المجموع", ship_label: "الشحن", total: "الإجمالي",
      discount: "الخصم", discount_code: "كود الخصم", apply: "تطبيق",
      discount_applied: "تم تطبيق خصم 5٪", discount_invalid: "كود الخصم غير صحيح أو منتهي",
      remove_code: "إلغاء الكود",
      checkout: "إتمام الطلب",
      note: "ادفع بطريقتك بعد تأكيد الطلب.",
      added: "اتضاف للعربة",
      min_order_message: "الحد الأدنى للطلب 200 جنيه. زوّد منتجات بقيمة {amount} جنيه علشان تكمل الدفع.",
    },
    checkout: {
      title: "إتمام الطلب",
      full_name: "الاسم بالكامل *", phone: "رقم التليفون *",
      whatsapp: "واتساب (لو مختلف)", email: "الإيميل (اختياري)",
      city: "المدينة *", area: "المنطقة *", address: "العنوان بالكامل *",
      address_notes: "مبنى / دور / شقة",
      delivery_time: "وقت التوصيل المفضل", delivery_ph: "مثلًا بعد ٦ مساءً",
      order_notes: "ملاحظات على الطلب",
      payment_method: "طريقة الدفع",
      payment_reference: "رقم العملية / المرجع",
      payment_reference_ph: "بعد ما تبعت الفلوس، اكتب رقم العملية هنا",
      payment_reference_optional: "(اختياري — ممكن تبعتهولنا بعدين على واتساب)",
      ref_phone: "رقم الموبايل اللي اتدفع منه",
      ref_phone_ph: "اكتب رقم التليفون اللي بعتت بيه الفلوس",
      ref_receipt: "رقم الإيصال",
      ref_receipt_ph: "رقم إيصال الدفع من فوري",
      pay_inst_title: "تعليمات الدفع",
      vodafone_inst: "حوّل {amount} جنيه على رقم فودافون كاش: {phone}",
      vodafone_step: "وبعدها اكتب رقم العملية تحت.",
      instapay_inst: "حوّل {amount} جنيه عن طريق إنستاباي على: {phone}",
      instapay_step: "وبعدها اكتب رقم العملية تحت.",
      fawry_inst: "حوّل {amount} جنيه على رقم محفظة فوري / الكارت الأصفر: {phone}",
      fawry_step: "وبعدها اكتب رقم العملية تحت.",
      cod_inst: "هتدفع {amount} جنيه كاش لما الكابتن يوصلك الطلب. مفيش دفع مقدم.",
      subtotal: "المجموع", ship_label: "الشحن", total: "الإجمالي",
      submit: "تأكيد الطلب", submitting: "جاري إرسال الطلب…",
      placed_toast: "تم تأكيد الطلب! 🎉",
    },
    confirm: {
      title: "تم تأكيد الطلب! 🐾",
      message_pre: "فريقنا هيتواصل معاك على", message_post: "عشان نأكد طلبك.",
      order_number: "رقم الطلب", status: "الحالة",
      customer: "العميل", city_area: "المدينة / المنطقة",
      shipping: "الشحن", payment: "الدفع", subtotal: "المجموع", total: "الإجمالي",
      pay_status: "حالة الدفع",
      next_steps: "الخطوات التالية",
      save_note: "احفظ رقم الطلب — هتحتاجه لما نتصل بيك.",
      continue: "تابع التسوق",
    },
    about: {
      tag: "ليه بيت مانشيز",
      title: "مصممة عشان أصحاب الحيوانات اللي بيهتمو.",
      intro: "بدأنا بيت مانشيز عشان لقاء مستلزمات حيوانات بسعر كويس في مصر لازم يبقى سهل. إحنا بنختار، إنت بتدلع.",
      trust: [
        ["مستلزمات موثوقة", "ماركات منتقاة من أطباء وخبراء."],
        ["توصيل في نفس اليوم", "توصيل في نفس اليوم في القاهرة والجيزة ومعظم المناطق القريبة."],
        ["دعم ودود", "ناس حقيقية على واتساب — احنا كمان بنحب الحيوانات."],
        ["قطط وكلاب بس", "تخصص واحد، مفيش تشتيت."],
        ["طلب سهل", "فودافون كاش، إنستاباي، فوري أو كاش عند الاستلام."],
        ["محبوب من ١٠ آلاف+", "ذيول مبسوطة وقطط بتخرخر من ٢٠٢٤."],
      ],
    },
    contact: {
      tag: "إحنا هنا",
      title_pre: "كلم", title_word: "إنسان حقيقي",
      subtitle: "أسئلة عن الأكل، اختيارات حسب السلالة، أو طلبك؟ كلمنا — عادة بنرد في خلال ساعة في مواعيد العمل.",
      whatsapp: "واتساب", phone: "تليفون", instagram: "إنستجرام", facebook: "فيسبوك",
      hours: "مواعيد العمل", sat_thu: "السبت – الخميس", friday: "الجمعة",
      open_247: "مفتوحين 24 ساعة", closed: "إجازة",
      outside: "خارج مواعيد العمل، ابعتلنا رسالة على واتساب — هنرد عليك أول الصبح. الطلبات المتأخرة بنراجعها عند فتح المحل.",
    },
    footer: { rights: "كل العلامات التجارية ملك أصحابها.", made: "صنعت بـ 🐾 في مصر." },
    payments: {
      "Vodafone Cash": "فودافون كاش",
      "Instapay": "إنستاباي",
      "FawryPay": "محفظة فوري / الكارت الأصفر",
      "Cash on Delivery": "كاش عند الاستلام",
      coming_soon: "قريبًا",
    },
    search: {
      placeholder: "دور على أكل، لعب، ماركات…",
      title: "دور على منتج", desc: "دور بالاسم أو الماركة أو الفئة أو الوصف.",
      searching: "جاري البحث…", no_results: "مفيش منتجات. جرب كلمة تانية.",
    },
    tracking: {
      nav: "تتبع الطلب",
      title: "تتبع طلبك", desc: "اكتب رقم الطلب ورقم التليفون اللي استخدمته عند الطلب.",
      order_number: "رقم الطلب", phone: "رقم التليفون",
      submit: "تتبع الطلب", searching: "جاري البحث…",
      not_found: "الطلب غير موجود. تأكد من رقم الطلب والتليفون.",
      payment_status: "حالة الدفع", shipping_status: "حالة الشحن",
      search_again: "تتبع طلب تاني",
    },
    banner: {
      text: "استخدم كود {code} واخصم 5٪ من أول طلب!",
      copy_cta: "نسخ الكود", copied: "تم نسخ الكود — تسوق سعيد! 🐾",
    },
    pdp: {
      back: "رجوع للمتجر", not_found: "المنتج غير موجود",
      not_found_desc: "ممكن المنتج ده اتشال أو اللينك غلط.",
      brand: "الماركة", size: "الحجم", weight: "الوزن", age_stage: "مناسب لـ",
      ingredients: "المكونات", feeding: "طريقة التقديم",
      details: "تفاصيل المنتج",
    },
    reviews: {
      title: "تقييمات العملاء", based_on: "بناءً على {n} تقييم", based_on_plural: "بناءً على {n} تقييمات",
      no_reviews: "لسه مفيش تقييمات. يلا كون أول واحد يشارك تجربته!",
      verified: "عملية شراء موثقة",
      purchase_hint: "التقييم متاح فقط للعملاء اللي استلموا طلبهم — هنبعتلك لينك التقييم بالإيميل بعد التوصيل.",
      your_name: "اسمك (يظهر للعامة)", your_rating: "تقييمك *", your_review: "رأيك *",
      submit: "إرسال التقييم", submitting: "جاري الإرسال…",
      thanks: "شكرًا! تقييمك تم استلامه وهيظهر بعد المراجعة.",
      rate_order_title: "قيّم طلبك", rate_order_desc: "شكرًا لتسوقك معنا يا {name}! قولنا رأيك في المنتجات.",
      already_reviewed: "قيّمت المنتج ده قبل كده — شكرًا!",
      invalid_title: "لينك التقييم غير موجود",
      invalid_desc: "اللينك ده غير صحيح أو منتهي. التقييم بيكون متاح فقط بعد توصيل الطلب.",
    },
  },
};

const LangContext = createContext(null);

export function LangProvider({ children }) {
  const [lang, setLang] = useState(() => {
    try {
      return localStorage.getItem("pm_lang") || "en";
    } catch (_) {
      return "en";
    }
  });

  useEffect(() => {
    try { localStorage.setItem("pm_lang", lang); } catch (_) {}
    document.documentElement.dir = lang === "ar" ? "rtl" : "ltr";
    document.documentElement.lang = lang;
  }, [lang]);

  const t = useCallback(
    (key, vars) => {
      const parts = key.split(".");
      let v = STRINGS[lang];
      for (const p of parts) v = v?.[p];
      if (v == null) {
        // fallback to english
        let en = STRINGS.en;
        for (const p of parts) en = en?.[p];
        v = en;
      }
      if (typeof v === "string" && vars) {
        return v.replace(/\{(\w+)\}/g, (_, k) => (vars[k] != null ? String(vars[k]) : `{${k}}`));
      }
      return v == null ? key : v;
    },
    [lang]
  );

  return (
    <LangContext.Provider value={{ lang, setLang, t, isRTL: lang === "ar" }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);

// Map known shipping ETA values (stored in DB in English) to the display text
// in the active language. Handles both legacy ("Same hour / same day") and
// current ("30 mins to 2 hours") values.
const ETA_MAP = {
  "30 mins - 2 hours": { en: "30 mins - 2 hours", ar: "من 30 دقيقة إلى ساعتين" },
  "1-3 hours": { en: "1-3 hours", ar: "من ساعة إلى 3 ساعات" },
  "2-4 hours": { en: "2-4 hours", ar: "من 2 إلى 4 ساعات" },
};

export function localizeEta(eta, lang) {
  if (!eta) return "";
  const entry = ETA_MAP[eta];
  if (!entry) return eta;
  return entry[lang] || entry.en || eta;
}
